import React, { FC, useState, useEffect, useCallback, useRef } from 'react';
import { LineChart, Line, ResponsiveContainer, YAxis } from 'recharts';
import { ArrowUpIcon, ArrowDownIcon } from '@heroicons/react/24/solid';
import { BoltIcon, ArrowsRightLeftIcon, CubeIcon } from '@heroicons/react/24/outline';
import { Gateway } from '../context/GatewayContext';
import { fetchMeterData } from '../services/backend/SrcfulPublicApi';
import SolarDataService, { SolarHistogramData, TimeframeOption } from '../services/SolarDataService';
import { fetchGatewayRewards, fetchCurrentMonthWalletRewards } from '../services/RewardsService';
import { useWalletContext } from '../context/WalletContext';
import { useTheme } from '../context/ThemeContext';
import { VisibilitySettings } from './dashboard/DashboardEditMenu';

// Small debugging component to help diagnose issues
const DebugInfo = ({ show, data }: { show: boolean, data: DebugData }) => {
  if (!show) return null;
  
  return (
    <div className="fixed bottom-4 right-4 bg-black text-white p-4 rounded-lg opacity-80 z-50 max-w-md max-h-96 overflow-auto">
      <h3 className="text-sm font-bold mb-2">Debug Info</h3>
      <pre className="text-xs">{JSON.stringify(data, null, 2)}</pre>
    </div>
  );
};

// Interface for debug data
interface DebugData {
  gateways: number;
  isLoading: boolean;
  dataFetched: boolean;
  error: string | null;
  errorObject?: any;
  fetchStarted?: number;
  fetchCompleted?: number;
  dersCount?: number;
  data?: {
    totalOutput?: number;
    totalConsumption?: number;
    totalRewards?: number;
    batteryCharge?: number;
    trendsLength: {
      output: number;
      consumption: number;
      rewards: number;
      battery?: number;
    };
  };
}

// Utility to convert watts to kilowatts
const wattsToKilowatts = (watts: number) => {
  return watts / 1000;
};

// Define time period options for the summary cards
export enum SummaryTimePeriod {
  ALL_TIME = 'ALL_TIME',
  THIRTY_DAYS = '30D',
  SEVEN_DAYS = '7D',
  TWENTY_FOUR_HOURS = '24H',
  CURRENT_MONTH = 'CURRENT_MONTH',
  NOW = 'NOW'
}

// Format number with commas
const formatNumber = (num: number, isInteger: boolean = false, title?: string, timePeriod?: SummaryTimePeriod): string => {
  // Format as integer if rewards or if specifically requested
  if (isInteger) {
    return Math.floor(num).toLocaleString();
  }
  
  // For Energy Consumption, always show 3 decimal places
  if (title === "Consumption") {
    return num.toLocaleString(undefined, { minimumFractionDigits: 3, maximumFractionDigits: 3 });
  }
  
  // For Production, always show 3 decimal places
  if (title === "Production") {
    return num.toLocaleString(undefined, { minimumFractionDigits: 3, maximumFractionDigits: 3 });
  }
  
  // For values less than 1, show more decimal places
  if (Math.abs(num) < 1) {
    return num.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  }
  
  // For other values, round to 1 decimal place
  return num.toLocaleString(undefined, { minimumFractionDigits: 1, maximumFractionDigits: 1 });
};

// Function to get range for All Time
const getAllTimeRange = () => {
  const now = new Date();
  const end = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  // Use a far past date for start to get "all time" data
  const start = new Date(2020, 0, 1);
  return { start, end };
};

// Function to get last 30 days range for rewards
const getLast30DaysRange = () => {
  const now = new Date();
  const end = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const start = new Date(end);
  start.setDate(start.getDate() - 30);
  return { start, end };
};

// Function to get previous 30 days range
const getPrevious30DaysRange = () => {
  const now = new Date();
  const current30End = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const current30Start = new Date(current30End);
  current30Start.setDate(current30Start.getDate() - 30);
  
  const prev30End = new Date(current30Start);
  prev30End.setDate(prev30End.getDate() - 1);
  const prev30Start = new Date(prev30End);
  prev30Start.setDate(prev30Start.getDate() - 30);
  
  return { start: prev30Start, end: prev30End };
};

// Function to get last 7 days range
const getLast7DaysRange = () => {
  const now = new Date();
  const end = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const start = new Date(end);
  start.setDate(start.getDate() - 7);
  return { start, end };
};

// Function to get previous 7 days range
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const getPrevious7DaysRange = () => {
  const now = new Date();
  const current7End = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const current7Start = new Date(current7End);
  current7Start.setDate(current7Start.getDate() - 7);
  
  const prev7End = new Date(current7Start);
  prev7End.setDate(prev7End.getDate() - 1);
  const prev7Start = new Date(prev7End);
  prev7Start.setDate(prev7Start.getDate() - 7);
  
  return { start: prev7Start, end: prev7End };
};

// Function to get last 24 hours range
const getLast24HoursRange = () => {
  const now = new Date();
  
  // Use rolling 24 hours instead of yesterday
  const end = new Date(now);
  const start = new Date(now);
  start.setHours(start.getHours() - 24);
  
  return { start, end };
};

// Get time range based on selected period
export const getTimeRangeForPeriod = (period: SummaryTimePeriod) => {
  switch (period) {
    case SummaryTimePeriod.ALL_TIME:
      return getAllTimeRange();
    case SummaryTimePeriod.THIRTY_DAYS:
      return getLast30DaysRange();
    case SummaryTimePeriod.SEVEN_DAYS:
      return getLast7DaysRange();
    case SummaryTimePeriod.TWENTY_FOUR_HOURS:
      return getLast24HoursRange();
    case SummaryTimePeriod.CURRENT_MONTH:
      return getCurrentMonthRange();
    default:
      return getLast30DaysRange(); // Default to 30 days
  }
};

// Get current month range (1st of the month to today)
export const getCurrentMonthRange = () => {
  const now = new Date();
  const start = new Date(now.getFullYear(), now.getMonth(), 1);
  const end = new Date(now);
  
  return { start, end };
};

// Get the short name of the current month (e.g., "Jan", "Feb")
export const getCurrentMonthShortName = () => {
  const now = new Date();
  return new Date(now.getFullYear(), now.getMonth(), 1).toLocaleString('en-US', { month: 'short' });
};

// Check if today is the first or second day of the month
export const isFirstOrSecondDayOfMonth = () => {
  const now = new Date();
  const dayOfMonth = now.getDate();
  return dayOfMonth <= 2;
};

// Special function for rewards time range calculations
export const getRewardsTimeRangeForPeriod = (period: SummaryTimePeriod) => {
  // Use UTC dates for consistency
  const now = new Date();
  const todayEnd = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate(), 23, 59, 59, 999));
  
  if (period === SummaryTimePeriod.SEVEN_DAYS) {
    // For 7D, use the last 7 days
    const end = new Date(now);
    const start = new Date(now);
    start.setUTCDate(start.getUTCDate() - 7);
    
    console.log('Rewards 7D view period:', {
      start: start.toISOString(),
      end: end.toISOString(),
      days: 7
    });
    
    return { start, end };
  } 
  
  if (period === SummaryTimePeriod.TWENTY_FOUR_HOURS) {
    // For 24H, use yesterday's full day (not the last 24 hours)
    const now = new Date();
    
    // Get yesterday's date
    const yesterday = new Date(now);
    yesterday.setDate(yesterday.getDate() - 1);
    
    // Set start to beginning of yesterday (00:00:00)
    const start = new Date(yesterday);
    start.setHours(0, 0, 0, 0);
    
    // Set end to end of yesterday (23:59:59)
    const end = new Date(yesterday);
    end.setHours(23, 59, 59, 999);
    
    console.log('Rewards 24H view period (yesterday):', {
      start: start.toISOString(),
      end: end.toISOString(),
      date: yesterday.toDateString()
    });
    
    return { start, end };
  } 
  
  if (period === SummaryTimePeriod.THIRTY_DAYS) {
    // 30D period calculation
    const end = new Date(todayEnd);
    const start = new Date(todayEnd);
    start.setUTCDate(start.getUTCDate() - 30);
    
    console.log('Rewards 30D view period:', {
      start: start.toISOString(),
      end: end.toISOString(),
      days: 30
    });
    
    return { start, end };
  }
  
  if (period === SummaryTimePeriod.CURRENT_MONTH) {
    // Current month period calculation (1st of month to now)
    const end = new Date(todayEnd);
    const start = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), 1, 0, 0, 0, 0));
    
    console.log('Rewards current month view period:', {
      start: start.toISOString(),
      end: end.toISOString(),
      month: start.toLocaleString('en-US', { month: 'short' })
    });
    
    return { start, end };
  }
  
  if (period === SummaryTimePeriod.ALL_TIME) {
    // For ALL_TIME, use a long period starting from 2020
    const end = new Date(todayEnd);
    const start = new Date(Date.UTC(2020, 0, 1, 0, 0, 0, 0)); // Start from 2020-01-01
    
    console.log('Rewards ALL_TIME view period:', {
      start: start.toISOString(),
      end: end.toISOString()
    });
    
    return { start, end };
  }
  
  // For all other periods, use the standard time ranges
  return getTimeRangeForPeriod(period);
};

// Get previous time range based on selected period for comparison
export const getPreviousTimeRangeForPeriod = (period: SummaryTimePeriod) => {
  // Use UTC dates for consistency
  const now = new Date();
  const todayEnd = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate(), 23, 59, 59, 999));
  
  if (period === SummaryTimePeriod.ALL_TIME) {
    // For ALL_TIME, just use the same full range (there's no "previous" to all time)
    const end = new Date(todayEnd);
    const start = new Date(Date.UTC(2020, 0, 1));
    
    return { start, end };
  }
  
  if (period === SummaryTimePeriod.THIRTY_DAYS) {
    // Previous 30 days = 30 days before the current 30 day period
    const current30End = new Date(todayEnd);
    const current30Start = new Date(current30End);
    current30Start.setUTCDate(current30Start.getUTCDate() - 30);
    
    // Previous period starts 30 days before current start
    const prev30Start = new Date(current30Start);
    prev30Start.setUTCDate(prev30Start.getUTCDate() - 30);
    
    // Previous period ends 1 day before current start
    const prev30End = new Date(current30Start);
    prev30End.setUTCDate(prev30End.getUTCDate() - 1);
    
    console.log('Previous 30D period:', {
      start: prev30Start.toISOString(),
      end: prev30End.toISOString(),
      days: 30
    });
    
    return { start: prev30Start, end: prev30End };
  }
  
  if (period === SummaryTimePeriod.CURRENT_MONTH) {
    // Previous month period calculation
    const currentYear = now.getUTCFullYear();
    const currentMonth = now.getUTCMonth();
    
    // Calculate previous month (handle January case)
    const prevMonth = currentMonth === 0 ? 11 : currentMonth - 1;
    const prevYear = currentMonth === 0 ? currentYear - 1 : currentYear;
    
    // Start: 1st day of previous month
    const start = new Date(Date.UTC(prevYear, prevMonth, 1, 0, 0, 0, 0));
    
    // End: last day of previous month
    const end = new Date(Date.UTC(currentYear, currentMonth, 0, 23, 59, 59, 999));
    
    console.log('Previous month period:', {
      start: start.toISOString(),
      end: end.toISOString(),
      month: start.toLocaleString('en-US', { month: 'short' })
    });
    
    return { start, end };
  }
  
  if (period === SummaryTimePeriod.SEVEN_DAYS) {
    // For 7D rewards view, calculate previous 7 days
    const now = new Date();
    const current7Start = new Date(now);
    current7Start.setUTCDate(current7Start.getUTCDate() - 7);
    
    const prev7End = new Date(current7Start);
    prev7End.setUTCDate(prev7End.getUTCDate() - 1);
    const prev7Start = new Date(prev7End);
    prev7Start.setUTCDate(prev7Start.getUTCDate() - 7);
    
    console.log('Previous 7D period:', {
      start: prev7Start.toISOString(),
      end: prev7End.toISOString(),
      days: 7
    });
    
    return { start: prev7Start, end: prev7End };
  }
  
  if (period === SummaryTimePeriod.TWENTY_FOUR_HOURS) {
    // For 24H, use previous 24 hours before the current 24 hour period
    const now = new Date();
    // We don't need current24End, so removing it
    const current24Start = new Date(now);
    current24Start.setHours(current24Start.getHours() - 24);
    
    // Previous period is 24 hours before the current period
    const prev24End = new Date(current24Start);
    const prev24Start = new Date(current24Start);
    prev24Start.setHours(prev24Start.getHours() - 24);
    
    console.log('Previous 24H period (rolling):', {
      start: prev24Start.toISOString(),
      end: prev24End.toISOString()
    });
    
    return { start: prev24Start, end: prev24End };
  }
  
  // Default to previous 30 days
  return getPrevious30DaysRange();
};

// Helper function to normalize rewards data for better visualization
const normalizeRewardsData = (data: Array<{ timestamp: string; value: number }>) => {
  if (!data || data.length === 0) return data;
  
  // Find min and max values
  const values = data.map(item => item.value);
  const minValue = Math.min(...values);
  const maxValue = Math.max(...values);
  
  // If all values are the same or nearly the same, create some variation
  if (maxValue - minValue < maxValue * 0.05) {
    // Create artificial variation when data is flat
    const baseValue = minValue * 0.95;
    return data.map((item, index) => ({
      timestamp: item.timestamp,
      value: baseValue + (item.value - baseValue) * (0.7 + (index / data.length) * 0.3)
    }));
  }
  
  // Otherwise, return original data
  return data;
};

interface SummaryCardProps {
  title: string;
  value: number | null;
  unit: string;
  change?: number | null;
  trendData: Array<{ timestamp: string; value: number }>;
  icon: React.ReactNode;
  isLoading?: boolean;
  onTimePeriodChange?: (period: SummaryTimePeriod) => void;
  selectedTimePeriod?: SummaryTimePeriod;
  showTimeSelector?: boolean;
  comparisonText?: string;
  isRewards?: boolean;
  isDemo?: boolean;
}

const SummaryCard: FC<SummaryCardProps> = ({
  title,
  value,
  unit,
  change,
  trendData,
  icon,
  isLoading = false,
  onTimePeriodChange,
  selectedTimePeriod = SummaryTimePeriod.THIRTY_DAYS,
  showTimeSelector = false,
  comparisonText = "from previous period",
  isRewards = false,
  isDemo = false
}) => {
  // Always use green for the chart line (as requested)
  const chartColor = '#22C55E'; // green
  const { theme } = useTheme();
  const isLightMode = theme === 'light';

  // Animation state for number rolling effect
  const [displayValue, setDisplayValue] = useState(value || 0);
  const [isAnimating, setIsAnimating] = useState(false);
  const [isIconGlowing, setIsIconGlowing] = useState(false);
  // New state for line glow effect
  const [lineGlowIntensity, setLineGlowIntensity] = useState(8);
  // Track initial loading state
  const [initialLoadComplete, setInitialLoadComplete] = useState(false);
  // Track if the value changed from a significant user action vs auto-refresh
  const [shouldAnimate, setShouldAnimate] = useState(false);
  // Get the current month's short name
  const [currentMonthName, setCurrentMonthName] = useState(getCurrentMonthShortName());

  // Detect auto-refresh by comparing previous timestamps
  const prevTimestamp = useRef(Date.now());
  
  // Update the month name when it changes (check on mount and when period changes)
  useEffect(() => {
    if (selectedTimePeriod === SummaryTimePeriod.CURRENT_MONTH) {
      setCurrentMonthName(getCurrentMonthShortName());
    }
  }, [selectedTimePeriod]);

  // Effect to detect if we should animate (manual refresh vs auto-refresh)
  useEffect(() => {
    // If period changes, we should animate
    setShouldAnimate(true);
  }, [selectedTimePeriod]);
  
  // Effect to check for month change (run daily at midnight)
  useEffect(() => {
    // Check for month change only if this is the rewards card
    if (!isRewards) return;
    
    const checkForMonthChange = () => {
      const newMonthName = getCurrentMonthShortName();
      if (newMonthName !== currentMonthName) {
        setCurrentMonthName(newMonthName);
      }
    };
    
    // Check when component mounts
    checkForMonthChange();
    
    // Set up interval to check daily (at midnight)
    const now = new Date();
    const tomorrow = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1);
    tomorrow.setHours(0, 0, 0, 0);
    const timeUntilMidnight = tomorrow.getTime() - now.getTime();
    
    // First timeout to trigger at midnight
    const midnightTimeout = setTimeout(() => {
      checkForMonthChange();
      
      // Then set up daily interval
      const dailyInterval = setInterval(checkForMonthChange, 24 * 60 * 60 * 1000);
      return () => clearInterval(dailyInterval);
    }, timeUntilMidnight);
    
    return () => clearTimeout(midnightTimeout);
  }, [isRewards, currentMonthName]);

  // Update displayValue with animation when the actual value changes
  useEffect(() => {
    if (value !== null) {
      const now = Date.now();
      const timeSinceLastUpdate = now - prevTimestamp.current;
      prevTimestamp.current = now;
      
      // On initial load, just set the value directly without animation
      if (!initialLoadComplete) {
        setDisplayValue(value);
        setInitialLoadComplete(true);
        return;
      }
      
      // For auto-refresh (happens roughly every 15 seconds), don't animate
      // Also skip animation if the value hasn't changed
      if (timeSinceLastUpdate < 20000 && !shouldAnimate && value === displayValue) {
        return;
      }
      
      // Reset shouldAnimate after using it
      if (shouldAnimate) {
        setShouldAnimate(false);
      }
      
      // Don't animate for very small changes during auto-refreshes
      const isSignificantChange = Math.abs(value - displayValue) > (isRewards ? 10 : 0.05);
      if (timeSinceLastUpdate < 20000 && !isSignificantChange && !shouldAnimate) {
        setDisplayValue(value);
        return;
      }
      
      // Proceed with animation for significant changes or manual updates
      setIsAnimating(true);
      
      // Trigger icon glow
      setIsIconGlowing(true);
      setTimeout(() => setIsIconGlowing(false), 1000);
      
      // Animate the value with a less aggressive rolling effect
      const start = displayValue;
      const end = value;
      const duration = 500; // 0.5 seconds instead of 1 second
      const frameRate = 15; // 15 fps instead of 30
      const totalFrames = duration / 1000 * frameRate;
      const increment = (end - start) / totalFrames;
      
      let frame = 0;
      const animateValue = () => {
        if (frame < totalFrames) {
          const newValue = start + increment * frame;
          // For rewards, ensure we're always using integer values during animation
          setDisplayValue(isRewards ? Math.floor(newValue) : newValue);
          frame++;
          requestAnimationFrame(animateValue);
        } else {
          setDisplayValue(end); // Ensure we end at the exact target value
          setIsAnimating(false);
        }
      };
      
      requestAnimationFrame(animateValue);
    }
  }, [value, displayValue, isRewards, initialLoadComplete, shouldAnimate]);

  // New effect to handle the line glow animation
  useEffect(() => {
    if (initialLoadComplete) {
      // Start with full glow intensity
      setLineGlowIntensity(8);
      
      // Fade out the glow after animation completes (animation duration is 1500ms)
      const fadeTimeout = setTimeout(() => {
        // Gradually fade out the glow
        const fadeInterval = setInterval(() => {
          setLineGlowIntensity(prev => {
            const newIntensity = prev - 0.5;
            if (newIntensity <= 0) {
              clearInterval(fadeInterval);
              return 0;
            }
            return newIntensity;
          });
        }, 50);
        
        return () => clearInterval(fadeInterval);
      }, 1500);
      
      return () => clearTimeout(fadeTimeout);
    }
  }, [initialLoadComplete, trendData]);

  return (
    <div className={`bg-white dark:bg-[#1D1D1D] rounded-lg border border-gray-200 dark:border-[#292929] p-4 shadow h-[280px] flex flex-col ${!initialLoadComplete && isLoading ? (isLightMode ? 'shimmer-effect light' : 'shimmer-effect dark') : ''} overflow-visible`}>
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <span className={`${isIconGlowing ? 'animate-pulse' : ''} text-green-600 dark:text-[#00FF84] transition-all duration-300 ${isIconGlowing ? 'drop-shadow-[0_0_8px_#00FF84]' : ''}`}>
            {icon}
          </span>
          <div className="flex items-center">
            <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300">{title}</h3>
            {isDemo && (
              <span className="ml-2 px-1.5 py-0.5 text-xs font-medium rounded bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300">
                Demo
              </span>
            )}
          </div>
        </div>
        
        {showTimeSelector && (
          <div className="flex text-xs">
            {title === "Production" ? (
              <>
                <button 
                  className={`px-2 py-1 rounded-l-lg transition ${selectedTimePeriod === SummaryTimePeriod.NOW ? 'bg-[#00FF84] text-black' : 'bg-gray-100 dark:bg-[#292929] text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-[#393939]'}`}
                  onClick={() => onTimePeriodChange && onTimePeriodChange(SummaryTimePeriod.NOW)}
                >
                  Now
                </button>
                <button 
                  className={`px-2 py-1 transition ${selectedTimePeriod === SummaryTimePeriod.THIRTY_DAYS ? 'bg-[#00FF84] text-black' : 'bg-gray-100 dark:bg-[#292929] text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-[#393939]'}`}
                  onClick={() => onTimePeriodChange && onTimePeriodChange(SummaryTimePeriod.THIRTY_DAYS)}
                >
                  30D
                </button>
                <button 
                  className={`px-2 py-1 rounded-r-lg transition ${selectedTimePeriod === SummaryTimePeriod.TWENTY_FOUR_HOURS ? 'bg-[#00FF84] text-black' : 'bg-gray-100 dark:bg-[#292929] text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-[#393939]'}`}
                  onClick={() => onTimePeriodChange && onTimePeriodChange(SummaryTimePeriod.TWENTY_FOUR_HOURS)}
                >
                  24H
                </button>
              </>
            ) : (
              <>
                <button 
                  className={`px-2 py-1 rounded-l-lg transition ${selectedTimePeriod === SummaryTimePeriod.NOW ? 'bg-[#00FF84] text-black' : 'bg-gray-100 dark:bg-[#292929] text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-[#393939]'}`}
                  onClick={() => onTimePeriodChange && onTimePeriodChange(SummaryTimePeriod.NOW)}
                >
                  {isRewards ? "Total" : "Now"}
                </button>
                {isRewards ? (
                  <button 
                    className={`px-2 py-1 transition ${selectedTimePeriod === SummaryTimePeriod.CURRENT_MONTH ? 'bg-[#00FF84] text-black' : 'bg-gray-100 dark:bg-[#292929] text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-[#393939]'} rounded-r-lg`}
                    onClick={() => onTimePeriodChange && onTimePeriodChange(SummaryTimePeriod.CURRENT_MONTH)}
                  >
                    {currentMonthName}
                  </button>
                ) : (
                  <button 
                    className={`px-2 py-1 transition ${selectedTimePeriod === SummaryTimePeriod.THIRTY_DAYS ? 'bg-[#00FF84] text-black' : 'bg-gray-100 dark:bg-[#292929] text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-[#393939]'}`}
                    onClick={() => onTimePeriodChange && onTimePeriodChange(SummaryTimePeriod.THIRTY_DAYS)}
                  >
                    30D
                  </button>
                )}
                {!isRewards && (
                  <button 
                    className={`px-2 py-1 rounded-r-lg transition ${selectedTimePeriod === SummaryTimePeriod.TWENTY_FOUR_HOURS ? 'bg-[#00FF84] text-black' : 'bg-gray-100 dark:bg-[#292929] text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-[#393939]'}`}
                    onClick={() => onTimePeriodChange && onTimePeriodChange(SummaryTimePeriod.TWENTY_FOUR_HOURS)}
                  >
                    24H
                  </button>
                )}
              </>
            )}
          </div>
        )}
      </div>
      
      <div className="flex-grow flex flex-col overflow-visible">
        {isLoading ? (
          <div className={`h-10 w-36 rounded-md ${isLightMode ? 'bg-gray-200' : 'bg-gray-700'} mb-2`}></div>
        ) : (
          <div className="flex items-baseline">
            <span className={`text-3xl font-bold text-gray-900 dark:text-white font-poly-sans-median ${isAnimating ? 'transition-all duration-300' : ''}`}>
              {displayValue !== null ? formatNumber(displayValue, isRewards, title, selectedTimePeriod) : '0'}
            </span>
            <span className="ml-1 text-gray-500 dark:text-gray-400">{unit}</span>
          </div>
        )}
        
        {!isLoading && change !== undefined && change !== null && (
          <div className="flex items-center mt-2">
            {change >= 0 ? (
              <ArrowUpIcon className="h-4 w-4 text-green-500 mr-1" />
            ) : (
              <ArrowDownIcon className="h-4 w-4 text-red-500 mr-1" />
            )}
            <span 
              className={`text-sm font-medium ${
                change >= 0 ? 'text-green-500' : 'text-red-500'
              }`}
            >
              {Math.abs(change).toFixed(1)}%
            </span>
            <span className="text-xs text-gray-500 dark:text-gray-400 ml-1">{comparisonText}</span>
          </div>
        )}
        
        <div className="h-[120px] mt-6 overflow-visible relative">
          {isLoading ? (
            <div className={`h-full rounded ${isLightMode ? 'bg-gray-200' : 'bg-gray-700'}`} style={{width: '100%'}}></div>
          ) : trendData && trendData.length > 0 ? (
            <div className="h-full w-full overflow-visible">
              <ResponsiveContainer width="100%" height="100%" className="overflow-visible">
                <LineChart
                  data={isRewards ? normalizeRewardsData(trendData) : trendData}
                  margin={{ top: 5, right: 10, bottom: 5, left: 0 }}
                  className="overflow-visible"
                >
                  {/* Add invisible YAxis with specific domain settings to normalize charts */}
                  <YAxis 
                    hide 
                    domain={isRewards ? 
                      // For rewards, set a domain that creates more visual variation
                      [(dataMin: number) => dataMin * 0.7, (dataMax: number) => dataMax * 1.1] : 
                      // For energy charts, allow auto scaling
                      ['auto', 'auto']
                    } 
                  />
                  
                  <Line
                    type="monotone"
                    dataKey="value"
                    stroke={chartColor}
                    strokeWidth={2}
                    dot={(props) => {
                      const { cx, cy, index } = props;
                      
                      // For Rewards, only show a dot at the last data point
                      if (isRewards) {
                        if (index === trendData.length - 1) {
                          return (
                            <circle 
                              cx={cx} 
                              cy={cy} 
                              r={5} 
                              fill="#00FF84" 
                              className="animate-pulse"
                              filter="url(#glowFilter)"
                              style={{ zIndex: 10 }}
                            />
                          );
                        }
                        return (
                          <circle 
                            cx={cx} 
                            cy={cy} 
                            r={0} 
                            fill="transparent" 
                          />
                        );
                      }
                      
                      // For other cards, show a pulsing dot at the last data point
                      if (index === trendData.length - 1) {
                        return (
                          <circle 
                            cx={cx} 
                            cy={cy} 
                            r={5} 
                            fill="#00FF84" 
                            className="animate-pulse"
                            filter="url(#glowFilter)"
                            style={{ zIndex: 10 }}
                          />
                        );
                      }
                      
                      // For other points, return an invisible dot
                      return (
                        <circle 
                          cx={cx} 
                          cy={cy} 
                          r={0} 
                          fill="transparent" 
                        />
                      );
                    }}
                    isAnimationActive={true}
                    animationDuration={1500}
                    animationEasing="ease-in-out"
                    filter="url(#lineGlowFilter)"
                  />
                  <defs>
                    <filter id="glowFilter" x="-100%" y="-100%" width="300%" height="300%">
                      <feGaussianBlur stdDeviation="3" result="blur" />
                      <feComposite in="SourceGraphic" in2="blur" operator="over" />
                    </filter>
                    {/* New filter for the line drop shadow effect with dynamic intensity */}
                    <filter id="lineGlowFilter" x="-30%" y="-30%" width="160%" height="160%">
                      <feGaussianBlur in="SourceAlpha" stdDeviation={lineGlowIntensity} result="blur" />
                      <feFlood floodColor="#00FF84" floodOpacity="0.6" result="color" />
                      <feComposite in="color" in2="blur" operator="in" result="shadow" />
                      <feMerge>
                        <feMergeNode in="shadow" />
                        <feMergeNode in="SourceGraphic" />
                      </feMerge>
                    </filter>
                  </defs>
                </LineChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <div className="h-full flex items-center justify-center">
              <span className="text-sm text-gray-400">No data available</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

interface DashboardSummaryCardsProps {
  gateways: Gateway[];
  visibilitySettings: VisibilitySettings;
}

// Create placeholder trend data for initial render
const createPlaceholderTrendData = () => {
  const data = [];
  const now = new Date();
  for (let i = 0; i < 30; i++) {
    const date = new Date();
    date.setDate(now.getDate() - (30 - i));
    data.push({
      timestamp: date.toISOString().split('T')[0],
      value: 0
    });
  }
  return data;
};

export const DashboardSummaryCards: FC<DashboardSummaryCardsProps> = ({ gateways, visibilitySettings }) => {
  // Initialize with default values (0) instead of null for immediate rendering
  const [totalOutput, setTotalOutput] = useState<number>(0);
  const [totalConsumption, setTotalConsumption] = useState<number>(0);
  const [totalRewards, setTotalRewards] = useState<number>(0);
  const [batteryCharge, setBatteryCharge] = useState<number>(0);
  const [outputTrend, setOutputTrend] = useState<Array<{ timestamp: string; value: number }>>(createPlaceholderTrendData());
  const [consumptionTrend, setConsumptionTrend] = useState<Array<{ timestamp: string; value: number }>>(createPlaceholderTrendData());
  const [rewardsTrend, setRewardsTrend] = useState<Array<{ timestamp: string; value: number }>>(createPlaceholderTrendData());
  const [batteryTrend, setBatteryTrend] = useState<Array<{ timestamp: string; value: number }>>(createPlaceholderTrendData());
  const [outputChange, setOutputChange] = useState<number>(0);
  const [consumptionChange, setConsumptionChange] = useState<number>(0);
  const [isBatteryDataDemo, setIsBatteryDataDemo] = useState<boolean>(false);
  const { publicKey } = useWalletContext();
  
  // Loading states for each card
  const [productionLoading, setProductionLoading] = useState(true);
  const [consumptionLoading, setConsumptionLoading] = useState(true);
  const [rewardsLoading, setRewardsLoading] = useState(true);
  const [batteryLoading, setBatteryLoading] = useState(true);
  
  // State for time periods
  const [productionTimePeriod, setProductionTimePeriod] = useState<SummaryTimePeriod>(SummaryTimePeriod.NOW);
  const [consumptionTimePeriod, setConsumptionTimePeriod] = useState<SummaryTimePeriod>(SummaryTimePeriod.NOW);
  const [rewardsTimePeriod, setRewardsTimePeriod] = useState<SummaryTimePeriod>(SummaryTimePeriod.CURRENT_MONTH);
  
  // Refs for tracking fetch operations
  const isFetchingProductionRef = useRef(false);
  const isFetchingConsumptionRef = useRef(false);
  const isFetchingBatteryRef = useRef(false);
  const isMounted = useRef(true);
  
  // Refs to prevent loading state flicker
  const loadingTimeoutsRef = useRef<Record<string, NodeJS.Timeout>>({});
  
  // Helper to set loading state with debounce
  const setDebouncedLoadingState = useCallback((
    key: string, 
    setLoadingFn: (loading: boolean) => void, 
    loading: boolean, 
    delay: number = 300
  ) => {
    // Clear any existing timeout for this key
    if (loadingTimeoutsRef.current[key]) {
      clearTimeout(loadingTimeoutsRef.current[key]);
    }
    
    if (!loading) {
      // For turning loading OFF, add a small delay to prevent flickering
      loadingTimeoutsRef.current[key] = setTimeout(() => {
        setLoadingFn(false);
        delete loadingTimeoutsRef.current[key];
      }, delay);
    } else {
      // For turning loading ON, do it immediately
      setLoadingFn(true);
    }
  }, []);

  // Initialize caches for each data type with different time periods
  const [productionCache, setProductionCache] = useState<Record<SummaryTimePeriod, {
    total: number;
    trend: Array<{ timestamp: string; value: number }>;
    change: number;
    lastUpdated: number;
  }>>({
    [SummaryTimePeriod.ALL_TIME]: { total: 0, trend: [], change: 0, lastUpdated: 0 },
    [SummaryTimePeriod.THIRTY_DAYS]: { total: 0, trend: [], change: 0, lastUpdated: 0 },
    [SummaryTimePeriod.SEVEN_DAYS]: { total: 0, trend: [], change: 0, lastUpdated: 0 },
    [SummaryTimePeriod.TWENTY_FOUR_HOURS]: { total: 0, trend: [], change: 0, lastUpdated: 0 },
    [SummaryTimePeriod.CURRENT_MONTH]: { total: 0, trend: [], change: 0, lastUpdated: 0 },
    [SummaryTimePeriod.NOW]: { total: 0, trend: [], change: 0, lastUpdated: 0 }
  });
  
  const [consumptionCache, setConsumptionCache] = useState<Record<SummaryTimePeriod, {
    total: number;
    trend: Array<{ timestamp: string; value: number }>;
    change: number;
    lastUpdated: number;
  }>>({
    [SummaryTimePeriod.ALL_TIME]: { total: 0, trend: [], change: 0, lastUpdated: 0 },
    [SummaryTimePeriod.THIRTY_DAYS]: { total: 0, trend: [], change: 0, lastUpdated: 0 },
    [SummaryTimePeriod.SEVEN_DAYS]: { total: 0, trend: [], change: 0, lastUpdated: 0 },
    [SummaryTimePeriod.TWENTY_FOUR_HOURS]: { total: 0, trend: [], change: 0, lastUpdated: 0 },
    [SummaryTimePeriod.CURRENT_MONTH]: { total: 0, trend: [], change: 0, lastUpdated: 0 },
    [SummaryTimePeriod.NOW]: { total: 0, trend: [], change: 0, lastUpdated: 0 }
  });
  
  // Add rewards cache
  const [rewardsCache, setRewardsCache] = useState<Record<SummaryTimePeriod, {
    total: number;
    trend: Array<{ timestamp: string; value: number }>;
    change: number;
    lastUpdated: number;
  }>>({
    [SummaryTimePeriod.ALL_TIME]: { total: 0, trend: [], change: 0, lastUpdated: 0 },
    [SummaryTimePeriod.THIRTY_DAYS]: { total: 0, trend: [], change: 0, lastUpdated: 0 },
    [SummaryTimePeriod.SEVEN_DAYS]: { total: 0, trend: [], change: 0, lastUpdated: 0 },
    [SummaryTimePeriod.TWENTY_FOUR_HOURS]: { total: 0, trend: [], change: 0, lastUpdated: 0 },
    [SummaryTimePeriod.CURRENT_MONTH]: { total: 0, trend: [], change: 0, lastUpdated: 0 },
    [SummaryTimePeriod.NOW]: { total: 0, trend: [], change: 0, lastUpdated: 0 }
  });
  
  // Cache time-to-live in milliseconds (5 minutes)
  const CACHE_TTL = 5 * 60 * 1000;
  
  // Use a ref instead of state for debugData to avoid re-render loops
  const debugDataRef = useRef<DebugData>({
    gateways: gateways.length,
    isLoading: true,
    dataFetched: false,
    error: null
  });
  
  console.log("DashboardSummaryCards rendered with gateways:", gateways.length);
  
  // Helper function to filter DERs by type
  const getFilteredDers = useCallback(() => {
    // Function to check if a DER is a validator
    const isValidatorDer = (der: any) => Boolean(der.validates);
    
    // Function to check if a gateway contains any validator DERs
    const isValidatorGateway = (gateway: Gateway) => {
      return gateway.ders?.some(isValidatorDer) || false;
    };
    
    // Find validator gateways for logging
    const validatorGateways = gateways.filter(isValidatorGateway);
    
    // Log validator gateways that are being excluded
    if (validatorGateways.length > 0) {
      console.log(`Excluding ${validatorGateways.length} validator gateways from production calculations:`, 
        validatorGateways.map(g => g.name || g.id.substring(0, 8))
      );
    }
    
    // Filter the gateways list to exclude validator gateways if needed
    const nonValidatorGateways = gateways.filter(gateway => !isValidatorGateway(gateway));
    
    // Collect all DERs from appropriate gateways based on whether to include validators
    const allDers = gateways.flatMap(gateway => 
      (gateway.ders || []).map(der => ({
        ...der,
        gatewayId: gateway.id,
        gatewayName: gateway.name
      }))
    );
    
    // Collect all DERs from non-validator gateways
    const nonValidatorDers = nonValidatorGateways.flatMap(gateway => 
      (gateway.ders || []).map(der => ({
        ...der,
        gatewayId: gateway.id,
        gatewayName: gateway.name
      }))
    );
    
    // Filter for solar DERs
    const allSolarDers = allDers.filter(der => 
      der.type.toLowerCase().includes('solar') || 
      der.type.toLowerCase().includes('pv')
    );
    
    // Filter for solar DERs from non-validator gateways
    const nonValidatorSolarDers = nonValidatorDers.filter(der => 
      der.type.toLowerCase().includes('solar') || 
      der.type.toLowerCase().includes('pv')
    );
    
    // Filter for meter DERs
    const meterDers = allDers.filter(der => 
      der.type.toLowerCase().includes('meter') || 
      der.type.toLowerCase().includes('energy')
    );
    
    return { 
      allDers, 
      solarDers: nonValidatorSolarDers, // Only use non-validator solar DERs for production
      allSolarDers, // Keep this for potential future use
      meterDers 
    };
  }, [gateways]);
  
  // Track component mounted state
  useEffect(() => {
    isMounted.current = true;
    
    // Capture the ref value at the time the effect runs
    const timeoutsRef = loadingTimeoutsRef.current;
    
    return () => {
      isMounted.current = false;
      
      // Use the captured ref value in the cleanup
      Object.entries(timeoutsRef).forEach(([key, timeoutId]) => {
        if (timeoutId) {
          clearTimeout(timeoutId);
        }
      });
    };
  }, []);

  // Fetch production data
  const fetchProductionData = useCallback(async (isAutoRefresh = false) => {
    // Prevent multiple parallel fetches
    if (isFetchingProductionRef.current) return;
    isFetchingProductionRef.current = true;
    
    // Track if we set loading state in this function call
    let didSetLoadingState = false;
    
    // Internal function to convert time period to SolarDataService timeframe
    const getTimeframeOption = (period: SummaryTimePeriod): TimeframeOption => {
      if (period === SummaryTimePeriod.TWENTY_FOUR_HOURS) return 'day';
      if (period === SummaryTimePeriod.SEVEN_DAYS) return 'week';
      return 'month'; // For 30d, month or all time, use monthly view
    };
    
    try {
      // Check cache first
      const cachedData = productionCache[productionTimePeriod];
      const now = Date.now();
      
      // If we have valid cached data that's not expired, use it
      // For NOW period, cache TTL is shorter (15 seconds) to ensure fresh data
      const currentCacheTTL = productionTimePeriod === SummaryTimePeriod.NOW ? 15000 : CACHE_TTL;
      
      if (cachedData && cachedData.lastUpdated > 0 && 
          now - cachedData.lastUpdated < currentCacheTTL && 
          cachedData.trend.length > 0) {
        console.log(`Using cached production data for ${productionTimePeriod}`);
        setTotalOutput(cachedData.total);
        setOutputTrend(cachedData.trend);
        setOutputChange(cachedData.change);
        setProductionLoading(false);
        return;
      }
      
      // No valid cache, need to fetch data
      // Only set loading state for manual refreshes, not auto-refreshes
      if (!isAutoRefresh) {
        setProductionLoading(true);
        didSetLoadingState = true;
      }
      
      const { solarDers, meterDers } = getFilteredDers();
      console.log("Solar DERs:", solarDers.length, "Meter DERs:", meterDers.length);
      
      // For "Now" we need to fetch the latest power data
      if (productionTimePeriod === SummaryTimePeriod.NOW) {
        // Initialize accumulators
        let currentTotalPower = 0;
        const trendData: Array<{ timestamp: string; value: number }> = [];
        
        // Fetch data for solar DERs for current power
        // Note: solarDers only includes DERs from non-validator gateways
        const solarPromises = solarDers.map(async (der) => {
          try {
            console.log(`Fetching solar data for ${der.sn}`);
            
            // Direct API call to get current power and 24h trend
            const solarData = await SolarDataService.getSolarData(
              der.gatewayId, 
              'day',
              '15m'
            );
            
            if (solarData?.latest) {
              // Get the latest power value directly from API (converting W to kW)
              const powerKw = wattsToKilowatts(solarData.latest.power_W);
              currentTotalPower += powerKw;
              
              // Get trend data directly from API
              if (solarData.histogram && solarData.histogram.length > 0) {
                solarData.histogram.forEach((item) => {
                  const timestamp = new Date(item.ts).toISOString();
                  const value = wattsToKilowatts(item.power_W);
                  
                  // Find or create an entry for this timestamp
                  const existingEntry = trendData.find(entry => entry.timestamp === timestamp);
                  if (existingEntry) {
                    existingEntry.value += value;
                  } else {
                    trendData.push({ timestamp, value });
                  }
                });
              }
            }
          } catch (error) {
            console.error(`Error fetching solar data for DER ${der.sn}:`, error);
          }
        });
        
        // Wait for all solar data fetches to complete
        await Promise.all(solarPromises);
        
        // Sort trend data by timestamp
        trendData.sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime());
        
        // For NOW, we don't calculate change percentage
        const changePercent = 0;
        
        console.log('Production NOW analysis:', {
          currentPower: currentTotalPower,
          trendDataPoints: trendData.length
        });
        
        // Update the cache with the new data
        setProductionCache(prev => ({
          ...prev,
          [SummaryTimePeriod.NOW]: {
            total: currentTotalPower,
            trend: trendData,
            change: changePercent,
            lastUpdated: Date.now()
          }
        }));
        
        // Update state
        setTotalOutput(currentTotalPower);
        setOutputTrend(trendData);
        setOutputChange(changePercent);
        setProductionLoading(false);
        isFetchingProductionRef.current = false;
        return;
      }
      
      // Get date ranges based on selected time period
      const productionRanges = {
        current: getTimeRangeForPeriod(productionTimePeriod),
        previous: getPreviousTimeRangeForPeriod(productionTimePeriod)
      };
      
      // Log the date ranges we're using
      console.log(`Fetching production data for ${productionTimePeriod}:`, {
        currentPeriod: { 
          start: productionRanges.current.start.toISOString(), 
          end: productionRanges.current.end.toISOString() 
        },
        previousPeriod: { 
          start: productionRanges.previous.start.toISOString(), 
          end: productionRanges.previous.end.toISOString() 
        }
      });
      
      // Initialize accumulators
      let currentProductionOutput = 0;
      let prevProductionOutput = 0;
      const outputByDay: Record<string, number> = {};
      
      // Fetch daily solar data for each DER for the 30-day period
      // Note: solarDers only includes DERs from non-validator gateways
      const solarPromises = solarDers.map(async (der) => {
        try {
          console.log(`Fetching solar data for ${der.sn}`);
          
          // For 30D view, specifically request daily data
          if (productionTimePeriod === SummaryTimePeriod.THIRTY_DAYS) {
            // Get the daily production data directly from API
            const dailyData = await SolarDataService.getDailySolarData(der.gatewayId);
            
            if (dailyData && dailyData.length > 0) {
              console.log(`Received ${dailyData.length} daily data points for ${der.sn}`);
              
              // Process each daily entry that falls within our 30-day window
              dailyData.forEach(day => {
                const date = new Date(day.ts);
                
                // Include this day's production if it falls within our current period
                if (date >= productionRanges.current.start && date <= productionRanges.current.end) {
                  // Add to the total production for the 30-day period
                  currentProductionOutput += day.power_kWh;
                  
                  // Add to daily breakdown for trend chart
                  const dateKey = day.ts.split('T')[0];
                  outputByDay[dateKey] = (outputByDay[dateKey] || 0) + day.power_kWh;
                }
                
                // Include this day's production if it falls within our previous period (for calculating change)
                if (date >= productionRanges.previous.start && date <= productionRanges.previous.end) {
                  prevProductionOutput += day.power_kWh;
                }
              });
            }
          } else {
            // For other time periods, use the standard approach
            const productionTimeframe = getTimeframeOption(productionTimePeriod);
            
            // Fetch current period data
            const solarCurrentData = await SolarDataService.getSolarData(
              der.gatewayId, 
              productionTimeframe,
              '1h', 
              productionRanges.current.start, 
              productionRanges.current.end
            );
            
            if (solarCurrentData) {
              // For 24H period, use today_kWh directly from the API
              if (productionTimePeriod === SummaryTimePeriod.TWENTY_FOUR_HOURS && typeof solarCurrentData.today_kWh === 'number') {
                currentProductionOutput += solarCurrentData.today_kWh;
                
                // Add to the trend data
                const now = new Date();
                const dateKey = now.toISOString().split('T')[0];
                outputByDay[dateKey] = (outputByDay[dateKey] || 0) + solarCurrentData.today_kWh;
              } 
              // Use dailykWh array from API if available
              else if (solarCurrentData.dailykWh && solarCurrentData.dailykWh.length > 0) {
                console.log(`Received ${solarCurrentData.dailykWh.length} daily entries for ${der.sn}`);
                
                // Process each daily entry
                solarCurrentData.dailykWh.forEach((dailyData: { ts: string; power_kWh: number }) => {
                  const date = new Date(dailyData.ts);
                  
                  if (date >= productionRanges.current.start && date <= productionRanges.current.end) {
                    // Add this day's production to the total
                    currentProductionOutput += dailyData.power_kWh;
                    
                    // Add to trend data
                    const dateKey = dailyData.ts.split('T')[0];
                    outputByDay[dateKey] = (outputByDay[dateKey] || 0) + dailyData.power_kWh;
                  }
                });
              }
              // Fallback to histogram data if needed
              else if (solarCurrentData.histogram && solarCurrentData.histogram.length > 0) {
                console.log(`Using histogram data with ${solarCurrentData.histogram.length} points for ${der.sn}`);
                
                // Process each histogram point
                const dailyTotals: Record<string, number> = {};
                
                solarCurrentData.histogram.forEach((item: SolarHistogramData) => {
                  const date = new Date(item.ts);
                  if (date >= productionRanges.current.start && date <= productionRanges.current.end) {
                    // Convert W to kWh based on time intervals in the histogram
                    // This is an approximation, the API may provide better values
                    const dateKey = item.ts.split('T')[0];
                    const powerKw = wattsToKilowatts(item.power_W);
                    
                    // Accumulate by day (for daily kWh estimation)
                    dailyTotals[dateKey] = (dailyTotals[dateKey] || 0) + powerKw;
                  }
                });
                
                // Add daily totals to the overall production and trend data
                Object.entries(dailyTotals).forEach(([dateKey, kWh]) => {
                  currentProductionOutput += kWh;
                  outputByDay[dateKey] = (outputByDay[dateKey] || 0) + kWh;
                });
              }
            }
            
            // Fetch previous period data for comparison
            const solarPrevData = await SolarDataService.getSolarData(
              der.gatewayId, 
              productionTimeframe,
              '1h', 
              productionRanges.previous.start, 
              productionRanges.previous.end
            );
            
            if (solarPrevData) {
              // Use daily kWh data for previous period if available
              if (solarPrevData.dailykWh && solarPrevData.dailykWh.length > 0) {
                solarPrevData.dailykWh.forEach((dailyData: { ts: string; power_kWh: number }) => {
                  const date = new Date(dailyData.ts);
                  if (date >= productionRanges.previous.start && date <= productionRanges.previous.end) {
                    prevProductionOutput += dailyData.power_kWh;
                  }
                });
              }
              // Fallback to histogram data for previous period
              else if (solarPrevData.histogram && solarPrevData.histogram.length > 0) {
                // Process previous period histogram data
                const prevDailyTotals: Record<string, number> = {};
                
                solarPrevData.histogram.forEach((item: SolarHistogramData) => {
                  const date = new Date(item.ts);
                  if (date >= productionRanges.previous.start && date <= productionRanges.previous.end) {
                    const dateKey = item.ts.split('T')[0];
                    const powerKw = wattsToKilowatts(item.power_W);
                    prevDailyTotals[dateKey] = (prevDailyTotals[dateKey] || 0) + powerKw;
                  }
                });
                
                // Sum up previous period totals
                Object.values(prevDailyTotals).forEach(kWh => {
                  prevProductionOutput += kWh;
                });
              }
            }
          }
        } catch (error) {
          console.error(`Error fetching solar data for DER ${der.sn}:`, error);
        }
      });
      
      // Wait for all solar data fetches to complete
      await Promise.all(solarPromises);
      
      // Convert the daily data into a trend array
      const trendData = Object.entries(outputByDay).map(([date, value]) => ({
        timestamp: date,
        value: value
      }));
      
      // Sort by date
      trendData.sort((a, b) => a.timestamp.localeCompare(b.timestamp));
      
      // Calculate percentage change using the API values directly
      const changePercent = prevProductionOutput > 0
        ? ((currentProductionOutput - prevProductionOutput) / prevProductionOutput) * 100
        : 0;
      
      console.log('Production analysis:', {
        currentOutput: currentProductionOutput,
        previousOutput: prevProductionOutput,
        changePercent: changePercent,
        numDays: trendData.length,
        period: productionTimePeriod
      });
      
      // Update the cache with the new data
      setProductionCache(prev => ({
        ...prev,
        [productionTimePeriod]: {
          total: currentProductionOutput,
          trend: trendData,
          change: changePercent,
          lastUpdated: Date.now()
        }
      }));
      
      // Update state with values from the API
      setTotalOutput(currentProductionOutput);
      setOutputTrend(trendData);
      setOutputChange(changePercent);
    } catch (error) {
      console.error('Error fetching production data:', error);
      debugDataRef.current = {
        ...debugDataRef.current,
        error: 'Error fetching production data',
        errorObject: error,
      };
    } finally {
      // Only update loading state if we previously set it
      if (didSetLoadingState) {
        setProductionLoading(false);
      }
      isFetchingProductionRef.current = false;
    }
  }, [productionTimePeriod, productionCache, getFilteredDers, CACHE_TTL]);

  // Fetch consumption data
  const fetchConsumptionData = useCallback(async (isAutoRefresh = false) => {
    // Prevent multiple parallel fetches
    if (isFetchingConsumptionRef.current) return;
    isFetchingConsumptionRef.current = true;
    
    try {
      // Check cache first
      const cachedData = consumptionCache[consumptionTimePeriod];
      const now = Date.now();
      
      // If we have valid cached data that's not expired, use it
      // For NOW period, cache TTL is shorter (15 seconds) to ensure fresh data
      const currentCacheTTL = consumptionTimePeriod === SummaryTimePeriod.NOW ? 15000 : CACHE_TTL;
      
      if (cachedData && cachedData.lastUpdated > 0 && 
          now - cachedData.lastUpdated < currentCacheTTL && 
          cachedData.trend.length > 0) {
        console.log(`Using cached consumption data for ${consumptionTimePeriod}`);
        setTotalConsumption(cachedData.total);
        setConsumptionTrend(cachedData.trend);
        setConsumptionChange(cachedData.change);
        setDebouncedLoadingState('consumption', setConsumptionLoading, false);
        isFetchingConsumptionRef.current = false;
        return;
      }
      
      // No valid cache, need to fetch data
      // Only set loading state for manual refreshes, not auto-refreshes
      if (!isAutoRefresh) {
        setDebouncedLoadingState('consumption', setConsumptionLoading, true);
      }
      
      const { meterDers } = getFilteredDers();
      console.log("Meter DERs for now consumption:", meterDers.length);
      
      if (meterDers.length === 0) {
        // No meters available
        if (isMounted.current) {
          setTotalConsumption(0);
          setConsumptionTrend([]);
          setConsumptionChange(0);
          setDebouncedLoadingState('consumption', setConsumptionLoading, false);
        }
        isFetchingConsumptionRef.current = false;
        return;
      }
      
      // For time periods other than NOW, generate mock consumption data
      if (consumptionTimePeriod !== SummaryTimePeriod.NOW) {
        console.log(`Generating mock consumption data for ${consumptionTimePeriod}`);
        
        // Generate mock data with a realistic pattern
        const trendData: Array<{ timestamp: string; value: number }> = [];
        const now = new Date();
        const daysToGenerate = consumptionTimePeriod === SummaryTimePeriod.TWENTY_FOUR_HOURS ? 24 : 30;
        let totalConsumption = 0;
        
        for (let i = 0; i < daysToGenerate; i++) {
          const date = new Date();
          const isHourly = consumptionTimePeriod === SummaryTimePeriod.TWENTY_FOUR_HOURS;
          
          if (isHourly) {
            date.setHours(now.getHours() - (daysToGenerate - i));
          } else {
            date.setDate(now.getDate() - (daysToGenerate - i));
          }
          
          // Create a value between 2 and 6 kWh with some variation
          const baseValue = 4;
          const variation = Math.sin(i / 3) * 2; // Creates a wave pattern
          const value = baseValue + variation + (Math.random() * 0.5);
          
          totalConsumption += value;
          
          trendData.push({
            timestamp: date.toISOString(),
            value: value
          });
        }
        
        // For comparison change, use a random value between -15% and +15%
        const changePercent = (Math.random() * 30) - 15;
        
        // Sort by timestamp
        trendData.sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime());
        
        // Update cache
        setConsumptionCache(prev => ({
          ...prev,
          [consumptionTimePeriod]: {
            total: totalConsumption,
            trend: trendData,
            change: changePercent,
            lastUpdated: Date.now()
          }
        }));
        
        // Update state
        setTotalConsumption(totalConsumption);
        setConsumptionTrend(trendData);
        setConsumptionChange(changePercent);
        setDebouncedLoadingState('consumption', setConsumptionLoading, false);
        isFetchingConsumptionRef.current = false;
        return;
      }
      
      // Get the latest power readings from all meters
      let totalCurrentPower = 0;
      const powerReadings: Array<{ timestamp: string; value: number }> = [];
      
      // Fetch current power data from meters
      const meterPromises = meterDers.map(async (der) => {
        try {
          const meterData = await fetchMeterData(der.sn);
          
          if (meterData && meterData.historical && meterData.historical.length > 0) {
            // Get the most recent power reading (in kW)
            const latestReading = meterData.historical[meterData.historical.length - 1];
            if (latestReading && typeof latestReading.consumption === 'number') {
              totalCurrentPower += latestReading.consumption;
              
              // Add to trend data for visualization
              powerReadings.push({
                timestamp: latestReading.ts,
                value: latestReading.consumption
              });
              
              // Also add historical readings from the last hour for trend
              const lastHourReadings = meterData.historical
                .slice(-12) // Last ~12 readings (assuming 10 minute intervals)
                .map(reading => ({
                  timestamp: reading.ts,
                  value: reading.consumption || 0
                }))
                .sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime());
              
              powerReadings.push(...lastHourReadings);
            }
          }
        } catch (error) {
          console.error(`Error fetching meter data for DER ${der.sn}:`, error);
        }
      });
      
      // Wait for all meter data fetching to complete
      await Promise.all(meterPromises);
      
      // Remove duplicates and sort by timestamp
      const uniqueReadings = Array.from(
        new Map(powerReadings.map(item => [item.timestamp, item])).values()
      ).sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime());
      
      if (isMounted.current) {
        console.log(`Updating current power consumption: ${totalCurrentPower} kW`);
        
        if (uniqueReadings.length > 0) {
          setConsumptionTrend(uniqueReadings);
          
          // Update cache with fresh data
          setConsumptionCache(prevCache => ({
            ...prevCache,
            [SummaryTimePeriod.NOW]: {
              total: totalCurrentPower,
              trend: uniqueReadings,
              change: 0, // No change percentage for real-time view
              lastUpdated: Date.now()
            }
          }));
        }
        
        setTotalConsumption(totalCurrentPower);
        setConsumptionChange(0); // No change percentage for real-time view
        setDebouncedLoadingState('consumption', setConsumptionLoading, false);
      }
    } catch (error) {
      console.error('Error fetching consumption data:', error);
      
      if (isMounted.current) {
        // In case of error, still show zero instead of leaving in loading state
        console.log("Error in consumption data fetch, showing zero values");
        const emptyTrendData = createPlaceholderTrendData().map(item => ({
          ...item,
          value: 0
        }));
        
        setTotalConsumption(0);
        setConsumptionTrend(emptyTrendData);
        setConsumptionChange(0);
        setDebouncedLoadingState('consumption', setConsumptionLoading, false);
      }
    } finally {
      isFetchingConsumptionRef.current = false;
    }
  }, [consumptionTimePeriod, consumptionCache, getFilteredDers, setDebouncedLoadingState, CACHE_TTL]);

  // Replace the battery API fetch with static mock data
  const fetchBatteryData = useCallback(async (isAutoRefresh = false) => {
    // Always use mock data for battery, no real API calls
    console.log('Using static mock data for battery');
    
    try {
      // Only set loading if this is a manual refresh, not an auto-refresh
      if (!isAutoRefresh) {
        setDebouncedLoadingState('battery', setBatteryLoading, true);
      }
      
      // Create static battery demo data
      const demoStateOfCharge = 60.0; // 60%
      
      // Create synthetic histogram data for the last 24h
      const now = new Date();
      const trend: Array<{ timestamp: string; value: number }> = [];
      
      // Create historic samples for the last 24 hours
      for (let i = 0; i < 24; i++) {
        const timestamp = new Date(now.getTime() - (24 - i) * 60 * 60 * 1000).toISOString();
        
        // Create a wavy pattern for the demo
        // This gives us a sine wave oscillating between 50% and 70%
        const hour = 23 - i; // hours ago (0-23)
        const value = 60 + 10 * Math.sin((hour / 6) * Math.PI);
        
        trend.push({ timestamp, value });
      }
      
      // Add the current value
      trend.push({ timestamp: now.toISOString(), value: demoStateOfCharge });
      
      // Sort by timestamp (should already be sorted, but just to be sure)
      trend.sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime());
      
      console.log('Using battery demo data');
      
      // Set demo flag to display "Demo" badge on the card
      setIsBatteryDataDemo(true);
      
      // Update state with demo data
      setBatteryCharge(demoStateOfCharge);
      setBatteryTrend(trend);
      setDebouncedLoadingState('battery', setBatteryLoading, false);
      
    } catch (error) {
      console.error('Error with battery mock data:', error);
      setDebouncedLoadingState('battery', setBatteryLoading, false);
    }
  }, [setDebouncedLoadingState]);

  // Fetch rewards data based on selected time period
  const fetchRewardsData = useCallback(async (isAutoRefresh = false) => {
    if (!publicKey) return;
    
    try {
      // Only set loading state for manual refreshes, not auto-refreshes
      if (!isAutoRefresh) {
        setDebouncedLoadingState('rewards', setRewardsLoading, true);
      }
      
      // Check cache first for rewards data
      const cachedData = rewardsCache[rewardsTimePeriod];
      const now = Date.now();
      
      // If we have valid cached data that's not expired, use it
      if (cachedData && cachedData.lastUpdated > 0 && 
          now - cachedData.lastUpdated < CACHE_TTL && 
          cachedData.trend.length > 0) {
        console.log(`Using cached rewards data for ${rewardsTimePeriod}`);
        setTotalRewards(cachedData.total);
        setRewardsTrend(cachedData.trend);
        setDebouncedLoadingState('rewards', setRewardsLoading, false);
        return;
      }
      
      // Total view - fetch from GraphQL API
      if (rewardsTimePeriod === SummaryTimePeriod.NOW || rewardsTimePeriod === SummaryTimePeriod.ALL_TIME) {
        console.log(`Fetching total rewards for wallet: ${publicKey}`);
        
        // GraphQL query for wallet rewards total
        const query = `{
          token {
            rewards {
              walletRewards(wallet: "${publicKey}")
            }
          }
        }`;
        
        // Make a POST request directly to the srcful API
        const response = await fetch('https://api.srcful.dev/', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ query })
        });
        
        if (!response.ok) {
          throw new Error(`API request failed: ${response.statusText}`);
        }
        
        const result = await response.json();
        
        if (result?.data?.token?.rewards?.walletRewards) {
          const totalRewardsValue = parseInt(result.data.token.rewards.walletRewards, 10) || 0;
          console.log(`Total rewards fetched for wallet ${publicKey}: ${totalRewardsValue} points`);
          
          // Create simple trend data
          const trendData = [{
            timestamp: new Date().toISOString().split('T')[0],
            value: totalRewardsValue
          }];
          
          setTotalRewards(totalRewardsValue);
          setRewardsTrend(trendData);
          
          // Update cache
          setRewardsCache(prev => ({
            ...prev,
            [SummaryTimePeriod.NOW]: {
              total: totalRewardsValue,
              trend: trendData,
              change: 0,
              lastUpdated: Date.now()
            }
          }));
          
          // We don't show percentage change for total views
          setDebouncedLoadingState('rewards', setRewardsLoading, false);
          return;
        }
      }
      
      // For CURRENT_MONTH, use the new endpoint
      if (rewardsTimePeriod === SummaryTimePeriod.CURRENT_MONTH) {
        console.log(`Fetching current month rewards for wallet: ${publicKey}`);
        
        // Use the new function to fetch current month rewards
        const currentMonthData = await fetchCurrentMonthWalletRewards(publicKey);
        
        if (currentMonthData) {
          const currentMonthRewards = currentMonthData.amount;
          console.log(`Current month rewards fetched for wallet ${publicKey}: ${currentMonthRewards} points from ${currentMonthData.numerOfGateways} gateways`);
          
          // Create a more meaningful trend visualization
          // Add points at the beginning and end of month
          const now = new Date();
          const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
          
          // Create trend data with two points to show current progress
          const trendData = [
            {
              timestamp: startOfMonth.toISOString().split('T')[0],
              value: 0
            },
            {
              timestamp: now.toISOString().split('T')[0],
              value: currentMonthRewards
            }
          ];
          
          setTotalRewards(currentMonthRewards);
          setRewardsTrend(trendData);
          
          // Update cache
          setRewardsCache(prev => ({
            ...prev,
            [SummaryTimePeriod.CURRENT_MONTH]: {
              total: currentMonthRewards,
              trend: trendData,
              change: 0,
              lastUpdated: Date.now()
            }
          }));
          
          setDebouncedLoadingState('rewards', setRewardsLoading, false);
          return;
        }
      }
      
      // For other periods, use the original implementation
      // Get appropriate time range for rewards based on selected period
      const { start, end } = getRewardsTimeRangeForPeriod(rewardsTimePeriod);
      const prevPeriod = getPreviousTimeRangeForPeriod(rewardsTimePeriod);
      
      console.log(`Fetching rewards for period ${rewardsTimePeriod}:`, {
        currentPeriod: { start: start.toISOString(), end: end.toISOString() },
        prevPeriod: { start: prevPeriod.start.toISOString(), end: prevPeriod.end.toISOString() }
      });
      
      // Fetch rewards for all gateways
      const rewardsData = await fetchGatewayRewards("", publicKey);
      
      // Check if data has been returned and is mounted
      if (!rewardsData || !isMounted.current) {
        setDebouncedLoadingState('rewards', setRewardsLoading, false);
        return;
      }
      
      // For other periods (including CURRENT_MONTH), aggregate rewards across all gateways
      const timeRange = getRewardsTimeRangeForPeriod(rewardsTimePeriod);
      
      console.log(`Calculating rewards for ${rewardsTimePeriod}:`, {
        period: rewardsTimePeriod,
        range: { 
          start: timeRange.start.toISOString(), 
          end: timeRange.end.toISOString() 
        }
      });
      
      // Calculate total rewards for the period across all gateways
      let totalRewardsForPeriod = 0;
      const pointsByDay: Record<string, number> = {};
      
      // Process each gateway's rewards
      rewardsData.betaDaily.forEach(gateway => {
        gateway.rewards
          .filter(reward => reward.wallet === publicKey)
          .forEach(reward => {
            const rewardDate = new Date(reward.ts);
            
            // Only include rewards that fall within the selected date range
            if (rewardDate >= timeRange.start && rewardDate <= timeRange.end) {
              totalRewardsForPeriod += reward.amount;
              
              // Group by day for chart data
              const dayKey = rewardDate.toISOString().split('T')[0];
              pointsByDay[dayKey] = (pointsByDay[dayKey] || 0) + reward.amount;
            }
          });
      });
      
      // Create trend data for chart visualization
      const trendData = Object.entries(pointsByDay)
        .map(([day, amount]) => ({ timestamp: day, value: amount }))
        .sort((a, b) => a.timestamp.localeCompare(b.timestamp));
      
      // Update state with calculated values
      setTotalRewards(totalRewardsForPeriod);
      setRewardsTrend(trendData);
      
      // We've removed percentage change from the Rewards card
      setDebouncedLoadingState('rewards', setRewardsLoading, false);
      
      // Update cache
      setRewardsCache(prev => ({
        ...prev,
        [rewardsTimePeriod]: {
          total: totalRewardsForPeriod,
          trend: trendData,
          change: 0,
          lastUpdated: Date.now()
        }
      }));
    } catch (error) {
      console.error('Error fetching rewards data:', error);
      setDebouncedLoadingState('rewards', setRewardsLoading, false);
    } finally {
      // No need to handle loading state here as we use the debounced version
    }
  }, [publicKey, rewardsTimePeriod, isMounted, rewardsCache, setDebouncedLoadingState, CACHE_TTL]);
  
  // Load rewards data when component mounts or time period changes (no polling)
  // Add a ref to prevent unnecessary fetches
  const fetchingRewardsRef = useRef(false);
  
  // Fetch production data when the time period changes
  useEffect(() => {
    if (visibilitySettings.production) {
      fetchProductionData(false);
    }
    // We intentionally exclude fetchProductionData from dependencies to avoid infinite loop due to changing reference
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productionTimePeriod, visibilitySettings.production]);

  // Fetch consumption data when the time period changes - with debounce to prevent flicker
  useEffect(() => {
    if (visibilitySettings.consumption) {
      // Only set loading if we don't have cached data
      const cachedData = consumptionCache[consumptionTimePeriod];
      const now = Date.now();
      const currentCacheTTL = consumptionTimePeriod === SummaryTimePeriod.NOW ? 15000 : CACHE_TTL;
      
      if (!(cachedData && cachedData.lastUpdated > 0 && 
          now - cachedData.lastUpdated < currentCacheTTL && 
          cachedData.trend.length > 0)) {
        // No valid cache, fetch new data
        fetchConsumptionData(false);
      } else {
        // We have valid cached data, use it without setting loading state
        setTotalConsumption(cachedData.total);
        setConsumptionTrend(cachedData.trend);
        setConsumptionChange(cachedData.change);
        setConsumptionLoading(false);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [consumptionTimePeriod, visibilitySettings.consumption, consumptionCache, CACHE_TTL]);
  
  // Fetch battery data only once on initial load
  useEffect(() => {
    if (visibilitySettings.batteryCharge && !batteryTrend.some(item => item.value > 0)) {
      // Only fetch mock data once if we don't have any
      fetchBatteryData(false);
    }
  }, [visibilitySettings.batteryCharge, batteryTrend, fetchBatteryData]);
  
  useEffect(() => {
    if (visibilitySettings.rewards && publicKey && !fetchingRewardsRef.current) {
      fetchingRewardsRef.current = true;
      fetchRewardsData().finally(() => {
        fetchingRewardsRef.current = false;
      });
    }
  }, [rewardsTimePeriod, publicKey, visibilitySettings.rewards, fetchRewardsData]);

  // Update debug data ref on relevant changes (no setState)
  useEffect(() => {
    debugDataRef.current = {
      ...debugDataRef.current,
      gateways: gateways.length,
      isLoading: productionLoading || consumptionLoading || rewardsLoading || batteryLoading,
      dataFetched: !productionLoading && !consumptionLoading && !rewardsLoading && !batteryLoading,
      data: {
        totalOutput: totalOutput,
        totalConsumption: totalConsumption,
        totalRewards: totalRewards,
        batteryCharge: batteryCharge,
        trendsLength: {
          output: outputTrend.length,
          consumption: consumptionTrend.length,
          rewards: rewardsTrend.length,
          battery: batteryTrend.length
        }
      }
    };
  }, [
    gateways.length,
    productionLoading, consumptionLoading, rewardsLoading, batteryLoading,
    totalOutput, totalConsumption, totalRewards, batteryCharge,
    outputTrend.length, consumptionTrend.length, rewardsTrend.length, batteryTrend.length
  ]);

  // Handle rewards time period change
  const handleRewardsTimePeriodChange = (period: SummaryTimePeriod) => {
    setRewardsTimePeriod(period);
  };

  // Get comparison text for the selected period
  const getComparisonText = (period: SummaryTimePeriod, isRewards: boolean = false): string => {
    switch (period) {
      case SummaryTimePeriod.ALL_TIME:
        return 'from all time';
      case SummaryTimePeriod.THIRTY_DAYS:
        return 'from previous 30d';
      case SummaryTimePeriod.SEVEN_DAYS:
        return 'from previous 7d';
      case SummaryTimePeriod.TWENTY_FOUR_HOURS:
        return 'from 24h ago';
      case SummaryTimePeriod.CURRENT_MONTH:
        return isRewards ? 'vs. previous period' : 'from previous month';
      default:
        return 'from previous period';
    }
  };

  // Get grid class based on number of visible cards
  const getGridClass = () => {
    const visibleCount = [
      visibilitySettings.production,
      visibilitySettings.consumption,
      visibilitySettings.batteryCharge,
      visibilitySettings.rewards
    ].filter(Boolean).length;
    
    if (visibleCount === 1) return "grid-cols-1";
    if (visibleCount === 2) return "grid-cols-1 md:grid-cols-2";
    if (visibleCount === 3) return "grid-cols-1 md:grid-cols-3";
    return "grid-cols-1 md:grid-cols-2 lg:grid-cols-4"; // Default for 4 cards
  };

  // Add refs for timers we need to clean up
  const refreshIntervalRef = useRef<number | null>(null);
  const initialTimeoutRef = useRef<number | null>(null);
  
  // Handle wallet disconnect event
  useEffect(() => {
    const handleWalletDisconnect = () => {
      console.log('Wallet disconnect detected, cancelling all data fetching operations');
      
      // Clear timers to prevent further API calls
      if (initialTimeoutRef.current !== null) {
        clearTimeout(initialTimeoutRef.current);
        initialTimeoutRef.current = null;
      }
      
      if (refreshIntervalRef.current !== null) {
        clearInterval(refreshIntervalRef.current);
        refreshIntervalRef.current = null;
      }
      
      // Clear loading states
      setProductionLoading(false);
      setConsumptionLoading(false); 
      setBatteryLoading(false);
      setRewardsLoading(false);
      
      // Reset fetching state flags
      isFetchingProductionRef.current = false;
      isFetchingConsumptionRef.current = false;
      isFetchingBatteryRef.current = false;
    };
    
    // Add event listener for wallet disconnect
    window.addEventListener('wallet-disconnect', handleWalletDisconnect);
    
    // Clean up the event listener
    return () => {
      window.removeEventListener('wallet-disconnect', handleWalletDisconnect);
    };
  }, []);
  
  // Set up synchronized polling for refreshing dashboard data - don't include battery
  useEffect(() => {
    // Initial fetch when component mounts - show loading indicators
    if (visibilitySettings.production) {
      fetchProductionData(false);
    }
    if (visibilitySettings.consumption) {
      fetchConsumptionData(false);
    }
    
    // Only fetch battery data once if battery visibility is enabled
    if (visibilitySettings.batteryCharge && !batteryTrend.some(item => item.value > 0)) {
      fetchBatteryData(false);
    }
    
    // Calculate time until next 15-second boundary to sync with table
    const now = new Date();
    const secondsUntilNextInterval = 15 - (now.getSeconds() % 15);
    const millisecondsUntilNextInterval = secondsUntilNextInterval * 1000 - now.getMilliseconds();
    
    // Set initial timeout to sync with 15-second boundaries
    initialTimeoutRef.current = window.setTimeout(() => {
      // Initial fetch at synchronized time (auto-refresh)
      if (isMounted.current) {
        console.log('Synced dashboard summary cards refresh');
        if (visibilitySettings.production && !isFetchingProductionRef.current) {
          fetchProductionData(true);
        }
        if (visibilitySettings.consumption && !isFetchingConsumptionRef.current) {
          fetchConsumptionData(true);
        }
        // Battery no longer refreshes
      }
      
      // Set up polling interval exactly on 15-second boundaries
      refreshIntervalRef.current = window.setInterval(() => {
        if (isMounted.current) {
          console.log('Polling dashboard summary data...');
          if (visibilitySettings.production && !isFetchingProductionRef.current) {
            fetchProductionData(true);
          }
          if (visibilitySettings.consumption && !isFetchingConsumptionRef.current) {
            fetchConsumptionData(true);
          }
          // Battery no longer refreshes
        }
      }, 15000); // 15 seconds - matching the Dashboard Table refresh interval
      
    }, millisecondsUntilNextInterval);
    
    // Store the current values of the timeouts at the time the effect runs
    const initialTimeout = initialTimeoutRef.current;
    const refreshInterval = refreshIntervalRef.current;
    
    // Clean up interval and timeout on unmount using the stored values
    return () => {
      if (initialTimeout !== null) {
        clearTimeout(initialTimeout);
      }
      if (refreshInterval !== null) {
        clearInterval(refreshInterval);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gateways, productionTimePeriod, consumptionTimePeriod, visibilitySettings]);

  // Add debug logging for component mount/unmount
  useEffect(() => {
    console.log('DashboardSummaryCards mounted');
    return () => {
      console.log('DashboardSummaryCards unmounted');
    };
  }, []);

  // Add debug logging for active fetch operations
  useEffect(() => {
    const debugInterval = setInterval(() => {
      console.log('Active fetch operations:', {
        production: isFetchingProductionRef.current,
        consumption: isFetchingConsumptionRef.current,
        battery: isFetchingBatteryRef.current,
        rewards: fetchingRewardsRef.current
      });
    }, 1000);
    return () => clearInterval(debugInterval);
  }, []);

  // Add debug logging for state updates
  useEffect(() => {
    console.log('DashboardSummaryCards state update:', {
      productionTimePeriod,
      consumptionTimePeriod,
      rewardsTimePeriod,
      cacheSize: {
        production: Object.keys(productionCache).length,
        consumption: Object.keys(consumptionCache).length,
        rewards: Object.keys(rewardsCache).length
      }
    });
  }, [productionTimePeriod, consumptionTimePeriod, rewardsTimePeriod, productionCache, consumptionCache, rewardsCache]);

  return (
    <>
      <div className={`grid ${getGridClass()} gap-4 mb-6`}>
        {visibilitySettings.production && (
          <SummaryCard
            title="Total Production"
            value={totalOutput}
            unit={productionTimePeriod === SummaryTimePeriod.NOW ? "kW" : "kWh"}
            trendData={outputTrend}
            change={productionTimePeriod === SummaryTimePeriod.NOW ? undefined : outputChange}
            icon={<BoltIcon className="h-5 w-5" />}
            isLoading={productionLoading}
            showTimeSelector={true}
            selectedTimePeriod={productionTimePeriod}
            onTimePeriodChange={setProductionTimePeriod}
            comparisonText={productionTimePeriod === SummaryTimePeriod.NOW ? undefined : getComparisonText(productionTimePeriod)}
          />
        )}
        {visibilitySettings.consumption && (
          <SummaryCard
            title="Total Consumption"
            value={totalConsumption}
            unit={consumptionTimePeriod === SummaryTimePeriod.NOW ? "kW" : "kWh"}
            trendData={consumptionTrend}
            change={consumptionTimePeriod === SummaryTimePeriod.NOW ? undefined : consumptionChange}
            icon={<ArrowsRightLeftIcon className="h-5 w-5" />}
            isLoading={consumptionLoading}
            showTimeSelector={true}
            selectedTimePeriod={consumptionTimePeriod}
            onTimePeriodChange={setConsumptionTimePeriod}
            comparisonText={consumptionTimePeriod === SummaryTimePeriod.NOW ? undefined : getComparisonText(consumptionTimePeriod)}
          />
        )}
        {visibilitySettings.batteryCharge && (
          <SummaryCard
            title="Battery Charge"
            value={batteryCharge}
            unit="%"
            trendData={batteryTrend}
            change={undefined}
            icon={<BoltIcon className="h-5 w-5" />}
            isLoading={batteryLoading}
            showTimeSelector={false}
            comparisonText="from 24h ago"
            isDemo={isBatteryDataDemo}
          />
        )}
        {visibilitySettings.rewards && (
          <SummaryCard
            title="Total Rewards Points"
            value={totalRewards}
            unit="Points"
            trendData={rewardsTrend}
            change={undefined} // Set to undefined to hide the change percentage
            icon={<CubeIcon className="h-5 w-5" />}
            isLoading={rewardsLoading}
            showTimeSelector={true}
            selectedTimePeriod={rewardsTimePeriod}
            onTimePeriodChange={handleRewardsTimePeriodChange}
            comparisonText={undefined} // Set to undefined to hide the comparison text
            isRewards={true}
          />
        )}
      </div>
      
      {/* Show debug info - set to false to disable debugging */}
      <DebugInfo 
        show={false} 
        data={debugDataRef.current} 
      />
    </>
  );
};